
import { random } from '@packages/toolbox';
import { useMeta } from 'quasar';
import { preFetch } from 'quasar/wrappers';
import { computed, defineComponent, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';

import NotFoundData from 'SharedComponents/ui/Images/NotFoundData.vue';
import { PublicUsefulLinksApi } from 'SharedDir/backend/api';
import { useDeviceSize } from 'SharedDir/common/composables/useDeviceSize';
import { useMetaPortal } from 'SharedDir/common/composables/useMetaPortal';
import { PaginationParams } from 'SharedDir/declarations/pagination';

import { useLoadingSsr } from 'PortalDir/common/composables/useLoadingSsr';
// import Filters from 'PortalDir/components/Filters.vue';
import SPage from 'PortalDir/components/page/SPage.vue';
import UsefulLinkItem from 'PortalDir/modules/UsefulLinks/components/Item.vue';
import UsefulLinksSkeletonItem from 'PortalDir/modules/UsefulLinks/components/SkeletonItem.vue';
import { usePublicUsefulLinksList } from 'PortalDir/modules/UsefulLinks/composables/usePublicUsefulLinksList';
import { useUsefulLinksStore } from 'stores/useful-links';

async function getData(usefulLinksStore, ssrContext, queryParam = {}) {
  const { paginationParams, filterParams, fillFromQueryStringSsr } = usePublicUsefulLinksList();

  fillFromQueryStringSsr(queryParam, paginationParams, filterParams);

  const params = {
    ...usefulLinksStore.pagination,
    ...paginationParams.value,
    ...usefulLinksStore.filters,
    ...filterParams.value,
  };
  const response = await PublicUsefulLinksApi.getPaginated(params);
  const updatedPaginationParams: PaginationParams<any> = {
    page: response.page,
    rowsPerPage: response.rowsPerPage,
    rowsNumber: response.rowsNumber,
    showDeleted: response.showDeleted,
    sortBy: response.sortBy,
    descending: response.descending,
  };
  usefulLinksStore.setUsefulLinksList(response.data);
  usefulLinksStore.setUsefulLinksPagination(updatedPaginationParams);
}

export default defineComponent({
  name: 'UsefulLinksPublicList',
  methods: { random },
  components: { UsefulLinkItem, SPage, NotFoundData, UsefulLinksSkeletonItem },
  props: {},

  preFetch: preFetch(async ({ store, ssrContext, currentRoute }) => {
    const usefulLinksStore = useUsefulLinksStore();
    await getData(usefulLinksStore, ssrContext, currentRoute?.query || {});
  }),

  setup() {
    const { isLoading, showLoading, loadingStore } = useLoadingSsr();
    const { t } = useI18n();

    const {
      paginationParams,
      filterParams,
      fetch,
      list,
      defaultFilterParams,
      setQueryParams,
      fillFromQueryString,
    } = usePublicUsefulLinksList();

    const usefulLinksStore = useUsefulLinksStore();
    const usefulLinksList = computed(() => {
      return usefulLinksStore.usefulLinksList;
    });

    const pagination = computed(() => {
      return usefulLinksStore.pagination || {};
    });

    async function applyFilter() {
      await setQueryParams({ ...filterParams.value, ...pagination.value });
      paginationParams.value = { ...pagination.value };
      await fetch();
      usefulLinksStore.setUsefulLinksList(list.value);
    }

    function clearFilterParam(param) {
      const defaultValue = defaultFilterParams();
      filterParams.value[param] = defaultValue[param];
    }

    async function proxyInputPagination() {
      await applyFilter();
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }

    const maxPagePagination = computed(() => {
      return Math.ceil((pagination.value?.rowsNumber || 1) / (pagination.value?.rowsPerPage || 1));
    });

    onMounted(async () => {
      fillFromQueryString(pagination, filterParams);
      await setQueryParams({ ...filterParams.value, ...pagination.value });
      isLoading.value = false;
    });

    const { clearFullPageUrl } = useMetaPortal();

    useMeta(() => {
      return {
        title: `Полезные ссылки - ${t('systemType')} ${t('systemShortName')}`,
        meta: {
          description: {
            name: 'description',
            template: () => `Полезные ссылки ${t('systemFullName')} (${t('systemShortName')})`,
          },
          keywords: {
            name: 'keywords',
            template: () =>
              [
                `${t('systemType')} ${t('systemShortName')}`,
                `${t('systemType')} ${t('systemFullName')}`,
                `Полезные ссылки ${t('systemShortName')}`,
                `Полезные ссылки ${t('systemFullName')}`,
                `Полезные ссылки ${t('regionShortName')}`,
                `Полезные ссылки ${t('regionFullName')}`,
                `Полезная информация ${t('systemShortName')}`,
                `Полезная информация ${t('systemFullName')}`,
                `Полезная информация ${t('regionShortName')}`,
                `Полезная информация ${t('regionFullName')}`,
              ].join(', '),
          },
        },
        link: {
          canonical: {
            rel: 'canonical',
            href: clearFullPageUrl.value,
          },
        },
      };
    });

    return {
      applyFilter,
      clearFilterParam,
      list,
      paginationParams,
      filterParams,
      defaultFilterParams,
      proxyInputPagination,
      maxPagePagination,
      pagination,
      usefulLinksList,
      ...useDeviceSize(),
      isLoading,
      showLoading,
    };
  },
});
